import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Compass,
  Mountain,
  Map,
  Database,
  Layers,
  BookOpen,
  TrendingUp,
  Target,
  Users,
  Zap,
  ArrowRight,
  Sparkles,
  Info,
  ChevronRight,
  Settings,
  SearchCode,
  Clock,
  Award,
  Activity,
  Lightbulb,
  Play,
  Home,
  Tent,
  Boxes,
  Code,
  Palette,
  FlaskConical,
  Library,
  FileText,
  Sliders
} from 'lucide-react';
import { journeyTracker } from '../lib/journeyTracker';
import { outcomeTracker } from '../lib/outcomeTracker';
import { mockTeamActivity } from '../data/mockTeamData';
import { RecentTeamStories } from '../components/team-widgets/RecentTeamStories';
import { YourRankBadge } from '../components/team-widgets/YourRankBadge';

type UserIntent = 'explore' | 'develop' | 'understand' | 'search' | 'learn' | null;

export const MissionControlArchived: React.FC = () => {
  const [selectedIntent, setSelectedIntent] = useState<UserIntent>(null);
  const [journey, setJourney] = useState<any>(null);
  const [hasVisited, setHasVisited] = useState(false);

  useEffect(() => {
    const currentJourney = journeyTracker.getJourney();
    setJourney(currentJourney);
    const visitedPages = (currentJourney as any).visitedPages;
    setHasVisited(visitedPages && visitedPages.length > 1);
  }, []);

  const journeyPaths = [
    {
      id: 'explore',
      title: 'Explore the Landscape',
      subtitle: 'Discover what capabilities exist',
      icon: Map,
      color: 'sage',
      gradient: 'from-sage-50 to-sage-100',
      borderColor: 'border-sage-400',
      description: 'Navigate the Island to discover the 12 capability domains. Perfect for first-time visitors and those seeking to understand the full landscape.',
      destinations: [
        { 
          path: '/elevation/island', 
          label: 'Island Explorer', 
          icon: Map,
          description: 'Interactive map of all 12 capability regions'
        },
        { 
          path: '/elevation/models', 
          label: 'Browse Models', 
          icon: Database,
          description: 'Card-based exploration of frameworks'
        }
      ],
      prompt: 'I want to see what\'s available...'
    },
    {
      id: 'develop',
      title: 'Develop Your Capabilities',
      subtitle: 'Track your progression from novice to expert',
      icon: Mountain,
      color: 'primary',
      gradient: 'from-consulting-slate-blue-50 to-consulting-slate-blue-100',
      borderColor: 'border-consulting-slate-blue-400',
      description: 'Climb the Mountain to track your journey through the 5 DEGAP stages. Choose your routes based on your role and see your elevation progress.',
      destinations: [
        { 
          path: '/elevation/mountain', 
          label: 'Mountain Ascent', 
          icon: Mountain,
          description: 'Vertical progression through capability levels'
        },
        { 
          path: '/elevation/templates', 
          label: 'Templates & Tools', 
          icon: Target,
          description: 'Ready-to-use resources for your current level'
        }
      ],
      prompt: 'I want to improve my skills...'
    },
    {
      id: 'understand',
      title: 'Understand the System',
      subtitle: 'See how everything fits together',
      icon: Layers,
      color: 'amber',
      gradient: 'from-amber-50 to-amber-100',
      borderColor: 'border-[var(--mustard)]',
      description: 'Explore the MOF Architecture to understand how the knowledge system is built. See the relationships between grammar, models, templates, and instances, plus the engines that power it all.',
      destinations: [
        { 
          path: '/elevation/about', 
          label: 'Knowledge System Overview', 
          icon: Layers,
          description: 'Complete system architecture and understanding'
        },
        { 
          path: '/elevation/grammar', 
          label: 'Meta-Stack Grammar', 
          icon: Database,
          description: 'The universal language for describing models'
        }
      ],
      prompt: 'I want to understand how it works...'
    },
    {
      id: 'search',
      title: 'Find Something Specific',
      subtitle: 'Search and filter across all content',
      icon: SearchCode,
      color: 'coral',
      gradient: 'from-coral-50 to-coral-100',
      borderColor: 'border-coral-400',
      description: 'Use advanced search to find specific frameworks, capabilities, or content. Filter by M3 criteria, category, or keyword.',
      destinations: [
        { 
          path: '/elevation/advanced-search', 
          label: 'Advanced Search', 
          icon: SearchCode,
          description: 'Multi-faceted search with filtering'
        },
        { 
          path: '/elevation/glossary', 
          label: 'Glossary', 
          icon: BookOpen,
          description: 'Canonical definitions and term lookup'
        }
      ],
      prompt: 'I\'m looking for something specific...'
    },
    {
      id: 'learn',
      title: 'Learn the Concepts',
      subtitle: 'Master the vocabulary and principles',
      icon: BookOpen,
      color: 'plum',
      gradient: 'from-purple-50 to-purple-100',
      borderColor: 'border-[var(--slate)]',
      description: 'Build your understanding through the glossary and design language. Learn terms both alphabetically and by conceptual families.',
      destinations: [
        { 
          path: '/elevation/glossary', 
          label: 'Glossary', 
          icon: BookOpen,
          description: 'Key terms and definitions'
        },
        { 
          path: '/elevation/design-language', 
          label: 'Design Language', 
          icon: Sparkles,
          description: 'Visual and verbal design principles'
        }
      ],
      prompt: 'I want to learn the fundamentals...'
    }
  ];

  const quickStats = [
    { label: 'Capability Dimensions', value: '12', icon: Target, color: 'text-sage-600' },
    { label: 'Frameworks & Models', value: '20+', icon: Database, color: 'text-consulting-slate-blue-600' },
    { label: 'DEGAP Stages', value: '5', icon: TrendingUp, color: 'text-[var(--mustard)]' },
    { label: 'Role Scopes', value: '4', icon: Users, color: 'text-coral-600' }
  ];

  const systemComponents = [
    {
      layer: 'Engines',
      description: 'Infrastructure powering the system',
      path: '/elevation/engines',
      icon: Settings,
      color: 'purple'
    },
    {
      layer: 'M3: Metadata',
      description: 'Universal language for models',
      path: '/elevation/grammar',
      icon: Layers,
      color: 'indigo'
    },
    {
      layer: 'M2: Models',
      description: 'Frameworks and systems',
      path: '/elevation/models',
      icon: Database,
      color: 'blue'
    },
    {
      layer: 'M1: Templates',
      description: 'Ready-to-deploy resources',
      path: '/elevation/templates',
      icon: Target,
      color: 'sage'
    },
    {
      layer: 'M0: Runtime',
      description: 'Real experiences & outcomes',
      path: '/elevation/runtime',
      icon: Zap,
      color: 'amber'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Archive Notice */}
      <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
        <div className="flex items-center gap-2 text-[var(--mustard)]">
          <Info className="h-5 w-5" />
          <p className="text-sm font-medium">
            This is an archived version of the Mission Control home page, preserved for reference.
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-charcoal-50 via-white to-charcoal-50 p-12 border-2 border-charcoal-200 text-center">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-consulting-slate-blue-400 blur-xl opacity-30 rounded-full"></div>
            <Tent className="h-20 w-20 text-consulting-slate-blue-600 relative z-10" />
          </div>
        </div>
        <h1 className="text-5xl font-bold text-charcoal-900 mb-4">
          Peak Priorities Base Camp
        </h1>
        <p className="text-xl text-charcoal-700 max-w-3xl mx-auto mb-6 leading-relaxed">
          Your central hub for navigating the knowledge system. Choose your journey below based on what you want to accomplish.
        </p>
        <div className="flex items-center justify-center gap-3 text-sm">
          <Info className="h-4 w-4 text-consulting-slate-blue-600" />
          <p className="text-consulting-slate-blue-800">
            <strong>New here?</strong> Start with "Explore the Landscape" to get oriented, or "Understand the System" to see the big picture.
          </p>
        </div>
      </div>

      {/* Master Organizing Framework Callout */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-[var(--mustard)] p-8 shadow-lg">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div className="h-16 w-16 bg-[var(--mustard)] flex items-center justify-center shadow-md">
              <Layers className="h-10 w-10 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="px-3 py-1 bg-[var(--mustard)] text-white text-xs font-semibold rounded-full uppercase tracking-wide">
                Master Organizing Framework
              </span>
              <span className="text-[var(--mustard)] font-mono text-xs bg-[var(--mustard-dim)] px-2 py-1 rounded">
                mof.strategy_delivery_cascade_by_scope
              </span>
            </div>
            <h3 className="text-2xl font-bold text-[var(--mustard)] mb-3">
              Strategy-Delivery Cascade by Scope
            </h3>
            <p className="text-[var(--mustard)] mb-4 leading-relaxed">
              The topmost organizing lens for all work — a systematic cascade from intent to impact. 
              Combines the 4 phases of the Strategy-Delivery Cascade (Strategy → Prioritization → Planning → Delivery) 
              with 2 scopes (Individual, Organization/Group) to create 8 distinct contexts. 
              <span className="font-semibold"> Prioritization acts as the spine connecting all four phases.</span>
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to="/elevation/entry/mof.strategy_delivery_cascade_by_scope"
                className="inline-flex items-center px-5 py-2.5 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors font-medium shadow-sm"
              >
                Explore Framework
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <div className="text-sm text-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2">
                <span className="font-semibold">4 Phases</span> × <span className="font-semibold">2 Scopes</span> = <span className="font-bold text-[var(--mustard)]">8 Contexts</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Dashboard - Personalized Section */}
      {hasVisited && journey && (
        <div className="bg-gradient-to-r from-charcoal-50 to-white p-6 border-2 border-charcoal-200">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6 flex items-center gap-2">
            <Activity className="h-6 w-6 text-consulting-slate-blue-600" />
            Your Journey
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Journey Stats */}
            <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-consulting-slate-blue-100 flex items-center justify-center">
                  <Target className="h-5 w-5 text-consulting-slate-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-charcoal-900">
                    {journey.visitedPages ? new Set(journey.visitedPages.map((p: any) => p.path)).size : 0}
                  </div>
                  <div className="text-xs text-charcoal-600">Pages Explored</div>
                </div>
              </div>
              <div className="text-xs text-charcoal-500">
                {journey.achievements ? journey.achievements.length : 0} achievements unlocked
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-sage-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-sage-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-charcoal-900">Recent Activity</div>
                  <div className="text-xs text-charcoal-600">
                    {journey.visitedPages && journey.visitedPages.length > 0
                      ? `Last visited: ${new Date(journey.visitedPages[journey.visitedPages.length - 1].timestamp).toLocaleDateString()}`
                      : 'No activity yet'}
                  </div>
                </div>
              </div>
              {journey.visitedPages && journey.visitedPages.length > 0 && (
                <Link
                  to={journey.visitedPages[journey.visitedPages.length - 1].path}
                  className="text-xs text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium flex items-center gap-1"
                >
                  <Play className="h-3 w-3" />
                  Continue where you left off
                </Link>
              )}
            </div>

            {/* Outcomes Stats */}
            <div className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[var(--mustard-dim)] flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-[var(--mustard)]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-charcoal-900">
                    {outcomeTracker.getOutcomes().length}
                  </div>
                  <div className="text-xs text-charcoal-600">Outcomes Tracked</div>
                </div>
              </div>
              <Link
                to="/elevation/outcomes"
                className="text-xs text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium flex items-center gap-1"
              >
                <Target className="h-3 w-3" />
                Track a new outcome
              </Link>
            </div>
          </div>

          {/* Personalized Recommendations */}
          <div className="mt-6 bg-[var(--bg-raised)] p-4 border-2 border-consulting-slate-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="h-5 w-5 text-[var(--mustard)]" />
              <h3 className="font-bold text-charcoal-900">Recommended Next Steps</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {journey.visitedPages && journey.visitedPages.some((p: any) => p.path.includes('/entry/')) && (
                <Link
                  to="/elevation/workshops"
                  className="flex items-center gap-3 p-3 bg-sage-50 border border-sage-200 hover:bg-sage-100 transition-colors group"
                >
                  <Award className="h-5 w-5 text-sage-600" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-charcoal-900">Try a Workshop</div>
                    <div className="text-xs text-charcoal-600">Practice what you've learned</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-sage-600 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              {outcomeTracker.getOutcomes().length === 0 && (
                <Link
                  to="/elevation/outcomes"
                  className="flex items-center gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors group"
                >
                  <Target className="h-5 w-5 text-[var(--mustard)]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-charcoal-900">Create Your First Outcome</div>
                    <div className="text-xs text-charcoal-600">Start tracking real-world results</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--mustard)] group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              {!journey.visitedPages || journey.visitedPages.filter((p: any) => p.category === 'team').length === 0 && (
                <Link
                  to="/elevation/team"
                  className="flex items-center gap-3 p-3 bg-charcoal-50 border border-charcoal-300 hover:bg-charcoal-100 transition-colors group"
                >
                  <Users className="h-5 w-5 text-charcoal-600" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-charcoal-900">Explore Team Stories</div>
                    <div className="text-xs text-charcoal-600">Learn from others' successes</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-charcoal-600 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              <Link
                to="/elevation/progress"
                className="flex items-center gap-3 p-3 bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 hover:bg-consulting-slate-blue-100 transition-colors group"
              >
                <Award className="h-5 w-5 text-consulting-slate-blue-600" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-charcoal-900">View Progress Dashboard</div>
                  <div className="text-xs text-charcoal-600">See your complete journey</div>
                </div>
                <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Your Rank Badge - Always visible for returning users */}
      {hasVisited && (
        <YourRankBadge metric="exploration" />
      )}

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        {quickStats.map((stat, idx) => {
          const StatIcon = stat.icon;
          return (
            <div key={idx} className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 text-center">
              <StatIcon className={`h-8 w-8 mx-auto mb-3 ${stat.color}`} />
              <div className="text-3xl font-bold text-charcoal-900 mb-1">{stat.value}</div>
              <div className="text-sm text-charcoal-600">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Journey Selection */}
      <div>
        <h2 className="text-3xl font-bold text-charcoal-900 mb-6 text-center">
          Choose Your Journey
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {journeyPaths.map(journey => {
            const JourneyIcon = journey.icon;
            const isSelected = selectedIntent === journey.id;
            
            return (
              <div
                key={journey.id}
                className={`
                   border-2 overflow-hidden cursor-pointer transition-all
                  ${isSelected 
                    ? `${journey.borderColor} shadow-xl scale-105` 
                    : 'border-[var(--rule)] hover:border-[var(--rule)] hover:shadow-lg'
                  }
                `}
                onClick={() => setSelectedIntent(isSelected ? null : journey.id as UserIntent)}
              >
                <div className={`bg-gradient-to-r ${journey.gradient} px-6 py-4 border-b-2 ${journey.borderColor}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <JourneyIcon className={`h-8 w-8 text-${journey.color}-600`} />
                    <h3 className="text-xl font-bold text-charcoal-900">{journey.title}</h3>
                  </div>
                  <p className="text-sm text-charcoal-600 italic">{journey.prompt}</p>
                </div>
                
                <div className="p-6 bg-[var(--bg-raised)]">
                  <p className="text-charcoal-700 mb-4">{journey.description}</p>
                  
                  {isSelected && (
                    <div className="space-y-3 animate-fadeIn">
                      <h4 className="font-semibold text-charcoal-900 text-sm uppercase tracking-wide">
                        Start Here:
                      </h4>
                      {journey.destinations.map((dest, idx) => {
                        const DestIcon = dest.icon;
                        return (
                          <Link
                            key={idx}
                            to={dest.path}
                            className={`
                              flex items-center gap-3 p-3  border-2 
                              bg-${journey.color}-50 border-${journey.color}-200 
                              hover:bg-${journey.color}-100 hover:border-${journey.color}-300
                              transition-all group
                            `}
                          >
                            <DestIcon className={`h-5 w-5 text-${journey.color}-600 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-charcoal-900 text-sm">{dest.label}</div>
                              <div className="text-xs text-charcoal-600">{dest.description}</div>
                            </div>
                            <ArrowRight className={`h-5 w-5 text-${journey.color}-600 flex-shrink-0 group-hover:translate-x-1 transition-transform`} />
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  
                  {!isSelected && (
                    <button className={`
                      w-full py-2  text-sm font-medium 
                      bg-${journey.color}-100 text-${journey.color}-800 
                      hover:bg-${journey.color}-200 transition-colors
                      flex items-center justify-center gap-2
                    `}>
                      Click to see paths
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* System Architecture Quick View */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-100 to-sage-100 p-8 border-2 border-consulting-slate-blue-300">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">
          System Architecture at a Glance
        </h2>
        <div className="grid md:grid-cols-5 gap-4">
          {systemComponents.map((component, idx) => {
            const ComponentIcon = component.icon;
            return (
              <Link
                key={idx}
                to={component.path}
                className={`
                  bg-[var(--bg-raised)]  p-4 border-2 border-${component.color}-200 
                  hover:border-${component.color}-400 hover:shadow-lg transition-all
                  text-center group
                `}
              >
                <ComponentIcon className={`h-8 w-8 mx-auto mb-3 text-${component.color}-600 group-hover:scale-110 transition-transform`} />
                <div className="font-bold text-charcoal-900 mb-1 text-sm">{component.layer}</div>
                <div className="text-xs text-charcoal-600">{component.description}</div>
              </Link>
            );
          })}
        </div>
        <div className="text-center mt-6">
          <Link
            to="/elevation/about"
            className="inline-flex items-center gap-2 px-6 py-3 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium"
          >
            <Layers className="h-5 w-5" />
            Explore Full Architecture
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Core System Sections */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Template Constructor */}
        <div className="bg-[var(--bg-raised)] p-6 border-2 border-consulting-slate-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <Boxes className="h-8 w-8 text-consulting-slate-blue-600" />
            <h2 className="text-2xl font-bold text-charcoal-900">Template Constructor</h2>
          </div>
          <p className="text-charcoal-700 mb-4">
            Codify existing knowledge into reusable templates that preserve meaning and enable consistent reuse.
          </p>
          <Link
            to="/elevation/grammar"
            className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
          >
            Explore Template Constructor
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Platform Engines */}
        <div className="bg-[var(--bg-raised)] p-6 border-2 border-[var(--mustard)]">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="h-8 w-8 text-[var(--mustard)]" />
            <h2 className="text-2xl font-bold text-charcoal-900">Platform Engines</h2>
          </div>
          <p className="text-charcoal-700 mb-4">
            Infrastructure layer that orchestrates the creation, transformation, storage, and delivery of knowledge objects.
          </p>
          <div className="flex gap-3">
            <Link
              to="/elevation/engines"
              className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
            >
              View Engines
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/elevation/operations"
              className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
            >
              View Operations
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Design Language */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-consulting-slate-blue-200">
        <div className="flex items-center gap-3 mb-4">
          <Palette className="h-8 w-8 text-consulting-slate-blue-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Design Language</h2>
        </div>
        <p className="text-charcoal-700 mb-4">
          Visual design language, components, and guidelines that ensure consistent brand expression across all materials.
        </p>
        <Link
          to="/elevation/design-language"
          className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
        >
          Explore Design Language
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Documentation & Resources */}
      <div className="bg-[var(--bg-raised)] p-6 border-2 border-charcoal-200">
        <div className="flex items-center gap-3 mb-4">
          <Library className="h-8 w-8 text-charcoal-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Documentation & Resources</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/elevation/glossary"
            className="flex items-center gap-3 p-4 border-2 border-charcoal-200 hover:border-consulting-slate-blue-300 hover:bg-consulting-slate-blue-50 transition-all group"
          >
            <BookOpen className="h-6 w-6 text-consulting-slate-blue-600" />
            <div>
              <div className="font-semibold text-charcoal-900">Glossary</div>
              <div className="text-sm text-charcoal-600">Key terms and definitions</div>
            </div>
            <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/elevation/documentation"
            className="flex items-center gap-3 p-4 border-2 border-charcoal-200 hover:border-consulting-slate-blue-300 hover:bg-consulting-slate-blue-50 transition-all group"
          >
            <FileText className="h-6 w-6 text-consulting-slate-blue-600" />
            <div>
              <div className="font-semibold text-charcoal-900">Documentation</div>
              <div className="text-sm text-charcoal-600">System documentation</div>
            </div>
            <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/elevation/site-map"
            className="flex items-center gap-3 p-4 border-2 border-charcoal-200 hover:border-consulting-slate-blue-300 hover:bg-consulting-slate-blue-50 transition-all group"
          >
            <Map className="h-6 w-6 text-consulting-slate-blue-600" />
            <div>
              <div className="font-semibold text-charcoal-900">Site Map</div>
              <div className="text-sm text-charcoal-600">Complete site structure</div>
            </div>
            <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600 ml-auto group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* The Lab */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-8 border-2 border-[var(--mustard)]">
        <div className="flex items-center gap-3 mb-6">
          <FlaskConical className="h-8 w-8 text-[var(--mustard)]" />
          <h2 className="text-2xl font-bold text-charcoal-900">The Lab</h2>
        </div>
        <p className="text-charcoal-700 mb-6">
          Experimental features and development tools. Early access to new capabilities and methodologies.
        </p>
        
        {/* Featured Projects */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Featured Projects</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { path: '/prioritization', label: 'Prioritization App', icon: Target },
              { path: '/elevation/lab/outcome-driver-generator', label: 'Outcome Driver Generator', icon: Target },
              { path: '/elevation/lab/clarity-compass', label: 'Clarity Compass', icon: Compass },
              { path: '/prioritization/tools', label: 'Prioritization Tools', icon: Sliders },
              { path: 'http://localhost:3102', label: 'Object Inspector', icon: SearchCode, isExternal: true },
              { path: '/elevation/lab/canon', label: 'Canon Engine', icon: BookOpen }
            ].map((item, idx) => {
              const ItemIcon = item.icon;
              if (item.isExternal) {
                return (
                  <a
                    key={idx}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-[var(--bg-raised)] border-2 border-[var(--mustard)] hover:border-[var(--mustard)] hover:shadow-md transition-all group"
                  >
                    <ItemIcon className="h-6 w-6 text-[var(--mustard)]" />
                    <div className="flex-1">
                      <div className="font-semibold text-charcoal-900">{item.label}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-[var(--mustard)] group-hover:translate-x-1 transition-transform" />
                  </a>
                );
              }
              return (
                <Link
                  key={idx}
                  to={item.path}
                  className="flex items-center gap-3 p-4 bg-[var(--bg-raised)] border-2 border-[var(--mustard)] hover:border-[var(--mustard)] hover:shadow-md transition-all group"
                >
                  <ItemIcon className="h-6 w-6 text-[var(--mustard)]" />
                  <div className="flex-1">
                    <div className="font-semibold text-charcoal-900">{item.label}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--mustard)] group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
        </div>

        {/* Lab Categories */}
        <div>
          <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Explore by Category</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Standalone Applications', path: '/elevation/lab' },
              { label: 'Canon Engine', path: '/elevation/lab' },
              { label: 'Studio Engine', path: '/elevation/lab' },
              { label: 'Knowledge Engine', path: '/elevation/lab' }
            ].map((category, idx) => (
              <Link
                key={idx}
                to={category.path}
                className="p-4 bg-[var(--bg-raised)] border-2 border-[var(--mustard)] hover:border-[var(--mustard)] hover:shadow-md transition-all text-center group"
              >
                <div className="font-semibold text-charcoal-900 group-hover:text-[var(--mustard)]">
                  {category.label}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/elevation/lab"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors font-medium"
          >
            <FlaskConical className="h-5 w-5" />
            View All Lab Features
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Suggested Pathways */}
      <div className="bg-[var(--bg-raised)] p-8 border-2 border-charcoal-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Suggested Learning Pathways</h2>
        <div className="space-y-4">
          <div className="border-l-4 border-sage-500 pl-4 py-2">
            <h3 className="font-bold text-charcoal-900 mb-1">For First-Time Visitors</h3>
            <p className="text-charcoal-700 text-sm mb-2">
              Start with <Link to="/elevation/about" className="text-consulting-slate-blue-600 hover:underline font-medium">Knowledge System Overview</Link> to understand the system, 
              then explore the <Link to="/elevation/island" className="text-sage-600 hover:underline font-medium">Island</Link> to discover capabilities, 
              finally dive into specific <Link to="/elevation/models" className="text-consulting-slate-blue-600 hover:underline font-medium">Models</Link> that interest you.
            </p>
          </div>
          
          <div className="border-l-4 border-consulting-slate-blue-500 pl-4 py-2">
            <h3 className="font-bold text-charcoal-900 mb-1">For Capability Development</h3>
            <p className="text-charcoal-700 text-sm mb-2">
              Use the <Link to="/elevation/mountain" className="text-consulting-slate-blue-600 hover:underline font-medium">Mountain Ascent</Link> to assess your current level, 
              choose your climbing routes, then access <Link to="/elevation/templates" className="text-sage-600 hover:underline font-medium">Templates</Link> matched to your elevation.
            </p>
          </div>
          
          <div className="border-l-4 border-[var(--mustard)] pl-4 py-2">
            <h3 className="font-bold text-charcoal-900 mb-1">For Finding Specific Content</h3>
            <p className="text-charcoal-700 text-sm mb-2">
              Use <Link to="/elevation/advanced-search" className="text-[var(--mustard)] hover:underline font-medium">Advanced Search</Link> with M3 filters, 
              or browse the <Link to="/elevation/glossary" className="text-consulting-slate-blue-600 hover:underline font-medium">Glossary</Link> to understand key terms and find related frameworks.
            </p>
          </div>
        </div>
      </div>

      {/* Team Section - Stories and Activity in 2-column layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Team Stories */}
        <div className="bg-[var(--bg-raised)] p-6 border-2 border-charcoal-200">
          <RecentTeamStories maxDisplay={3} compact={true} />
        </div>

        {/* Team Activity Feed */}
        {mockTeamActivity.length > 0 && (
          <div className="bg-[var(--bg-raised)] p-6 border-2 border-charcoal-200">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-charcoal-600" />
              Recent Team Activity
            </h2>
            <div className="space-y-3">
              {mockTeamActivity.slice(0, 3).map((activity) => (
                <div key={activity.id} className="flex gap-3 p-3 bg-charcoal-50 border border-charcoal-200">
                  <div className="w-8 h-8 rounded-full bg-charcoal-200 flex items-center justify-center flex-shrink-0">
                    <Activity className="h-4 w-4 text-charcoal-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal-900">
                      <span className="font-semibold">{activity.actor.name}</span>
                      {' '}
                      <span className="text-charcoal-600">{activity.title}</span>
                    </p>
                    <p className="text-xs text-charcoal-600 mt-1 line-clamp-2">{activity.description}</p>
                    <p className="text-xs text-charcoal-500 mt-1">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/elevation/team"
                className="inline-flex items-center gap-2 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
              >
                View all team activity
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};









